import * as _ from 'lodash';
import * as dtoHelper from './groupNameDtoHelper';

import { IValueViewModel, GroupNameDto, Group, StringReplacement, ObjectType, ErrorLevel } from '../../models';
import { WideError, WideErrorParam } from '../../common';
import { missingSeries } from '../constants';
import { ISeriesConfiguration } from '../../widgets';
import { IBindingResultError } from '../../services';

export interface VmMapping {
    originalVm: IValueViewModel;
    newVm: IValueViewModel;
}

/**
 *  Retrieves a list of group name dtos from a list of valueViewModels.
 * @param vms The list of vms to search in.
 */
export function getGroupNameDtos(vms: IValueViewModel[]): GroupNameDto[] {
    const groups: GroupNameDto[] = [];
    vms.forEach(v => {
        if (v.groupList && v.groupList.length > 0) {
            v.groupList.forEach(gl => {
                const found = groups.filter(gls => gls.uniqueName === gl.uniqueName);
                if (found && found.length === 0) {
                    groups.push(gl);
                }
            });
        }
    });
    return groups;
}

/**
 * Retrieves view models corresponding to a given list of view models according to replacements in name and group unique name.
 * @param originals The list of view models to remap.
 * @param global The global list of view models in which to search.
 * @param seriesRename The list of repalcements in identifier names.
 * @param groupsRename The list of replacements in groups unique name.
 */
export function renameViewModels(originals: IValueViewModel[],
    global: IValueViewModel[],
    seriesRename: StringReplacement[],
    groupsRename: StringReplacement[]
): { replaced: VmMapping[], notFound: VmMapping[] } {
    const replaced: VmMapping[] = [];
    const notFound: VmMapping[] = [];
    const groups = getGroupNameDtos(global);
    originals.forEach(vm => {
        let newName = vm.name;
        seriesRename.forEach(r => {
            newName = newName.replace(r.oldValue, r.newValue);
        });
        let newVm: IValueViewModel;
        const withSameName = global.filter(vm => vm.name === newName);
        let group = vm.groupList[0];
        if (vm.objectType === ObjectType.Identifier && group) {
            group = dtoHelper.getNewGroup(group, groups, groupsRename);
            newVm = withSameName.find(d => (d.groupList && d.groupList[0] && d.groupList[0].uniqueName === group.uniqueName));
        } else {
            if (withSameName.length) {
                newVm = withSameName[0];
            }
        }
        if (newVm) {
            replaced.push({ originalVm: vm, newVm: newVm });
        } else {
            notFound.push({ originalVm: vm, newVm: { name: newName } as IValueViewModel });
        }
    });
    return { replaced: replaced, notFound: notFound };
}

export function renameGroups(originals: Group[], global: Group[], groupsRename: StringReplacement[]): { replaced: VmMapping[], notFound: VmMapping[] } {
    const replaced: VmMapping[] = [];
    const notFound: VmMapping[] = [];

    originals.forEach(g => {
        let group = g;
        let newGroupName = g.uniqueName;

        if (groupsRename && groupsRename.length) {
            groupsRename.forEach(n => newGroupName = newGroupName.replace(n.oldValue, n.newValue));
            group = global.find(g => g.uniqueName === newGroupName);
        }

        if (group) {
            replaced.push({ originalVm: { name: g.name } as IValueViewModel, newVm: { name: group.name } as IValueViewModel });
        } else {
            notFound.push({ originalVm: { name: g.name } as IValueViewModel, newVm: { name: newGroupName } as IValueViewModel });
        }
    });

    return { replaced: replaced, notFound: notFound };
}

/**
 * Converts a list of view models to a dictionary indexed by guid.
 * @param vms The list of view models to convert.
 */
export function toDictionary<T extends { guid: string }>(vms: T[]): { [guid: string]: T } {
    const dic = {};
    if (vms) {
        vms.forEach(vm => dic[vm.guid] = vm);
    }
    return dic;
}

/**
 * Checks a list of series for existence against a dictionary of view models.
 * @param seriesConfig The list of series to check.
 * @param dic The view models against which series have to be checked.
 */
export function checkSeries(seriesConfig: ISeriesConfiguration[], dic: { [guid: string]: IValueViewModel }): WideError | undefined {
    let error: WideError;
    if (_.some(seriesConfig, s => !dic[s.guid])) {
        error = new WideError(ErrorLevel.Error, missingSeries);
    }
    return error;
}

export function checkVms(guids: string[], dic: { [guid: string]: IValueViewModel }): WideError | undefined {
    let error: WideError;
    if (_.some(guids, s => !dic[s])) {
        error = new WideError(ErrorLevel.Error, missingSeries);
    }
    return error;
}

export interface SeriesConfig {
    guid: string;
    isQuery?: boolean;
}

export function bindingResultErrorToWideError(errors: IBindingResultError[]): WideError {
    const errorArr = [];
    const params = [];

    errors.forEach(e => {
        errorArr.push('Object _path_ > _name_ does not exist');
        const errorName = e.vm ? e.vm.name : e.group ? e.group.name : 'Unknown';
        params.push({ path: e.newPath, name: errorName } as WideErrorParam);
    });

    const errorString = errorArr.join('\r\n');
    return errors.length > 0 ? new WideError(ErrorLevel.Info, errorString, params) : null;
}

export function mapConfiguration(
    seriesConfig: SeriesConfig[],
    dic: { [guid: string]: IValueViewModel },
    map: { [guid: string]: string },
    errors?: { [guid: string]: IBindingResultError }
) {
    const mapped = seriesConfig.map(s => {
        const guid = !!map && !!map[s.guid] ? map[s.guid] : s.guid;
        const vm = dic[guid];
        return {
            configuration: s,
            viewModel: vm,
            missing: !vm && !s.isQuery
        };
    });

    let highest = null;

    if (errors) {
        const bindMissing = mapped.filter(m => errors[m.configuration.guid])
            .map(m => errors[m.configuration.guid]);

        highest = bindingResultErrorToWideError(bindMissing);
    }

    if (mapped.filter(m => m.missing && (!errors || !errors[m.configuration.guid])).length > 0) {
        highest = new WideError(ErrorLevel.Error, missingSeries);
    }

    return { error: highest, series: mapped };
}